import type { ViewQueryNode, ViewQueryRecordNode } from '@salescore/core'
import { getInnerRowSpan } from '@salescore/core'

import type { RSheetColumn, RSheetRecordNode } from '../../domain/column'
import { convertRelationToNodeType } from '../../recoil/selectors/useSheetColumns'
// eslint-disable-next-line sonarjs/deprecation
import { getRecordNodeFromRootNode } from '../../state/nodeUtility'

export interface RecordNodeWithInnerRowIndex {
  innerRowIndexStart: number
  innerRowIndexEnd: number
  recordNode: undefined | RSheetRecordNode
  parentRecordNode: undefined | RSheetRecordNode // rootのときにundefined
  height: number
}

export function getRecordNodesWithInnerRowIndex(
  row: RSheetRecordNode,
  node: ViewQueryNode,
): RecordNodeWithInnerRowIndex[] {
  const nodeType = convertRelationToNodeType(node)
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: なぜかrowがないことがありうるようなので、検査する
  //  →意味がわからないがそういうコメントがあるので残しておく
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (row === undefined || row === null) {
    return []
  }

  // rootのとき
  if (nodeType === 'root') {
    return [
      {
        innerRowIndexStart: 0,
        innerRowIndexEnd: getInnerRowSpan(row),
        recordNode: row,
        parentRecordNode: undefined,
        height: getInnerRowSpan(row),
      },
    ]
  }

  const parentRecordNodes = getParentRecordNodes(row, node.path)
  if (parentRecordNodes === undefined) {
    // 親が一切いない子など
    return []
  }

  const recordNodesWithIndex = parentRecordNodes.flatMap((parentRecordNode): RecordNodeWithInnerRowIndex[] => {
    // 子がいない場合、空セルを親の高さで作る
    if (parentRecordNode.children.length === 0) {
      return [
        {
          innerRowIndexStart: parentRecordNode.parent.meta.innerRowIndexStart,
          innerRowIndexEnd: parentRecordNode.parent.meta.innerRowIndexEnd,
          recordNode: undefined,
          parentRecordNode: parentRecordNode.parent,
          height: getInnerRowSpan(parentRecordNode.parent),
        },
      ]
    }

    // parentな子が1つだけある場合、親の高さで作る
    if (nodeType === 'parent' && parentRecordNode.children.length === 1) {
      return [
        {
          innerRowIndexStart: parentRecordNode.parent.meta.innerRowIndexStart,
          innerRowIndexEnd: parentRecordNode.parent.meta.innerRowIndexEnd,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          recordNode: parentRecordNode.children[0]!,
          parentRecordNode: parentRecordNode.parent,
          height: getInnerRowSpan(parentRecordNode.parent),
        },
      ]
    }

    return parentRecordNode.children.map((recordNode) => ({
      innerRowIndexStart: recordNode.meta.innerRowIndexStart,
      innerRowIndexEnd: recordNode.meta.innerRowIndexEnd,
      recordNode,
      parentRecordNode: parentRecordNode.parent,
      height: getInnerRowSpan(recordNode),
    }))
  })

  // 親がいないセルに対して、blankCellを描画するために穴埋めする
  return addBlankCell(recordNodesWithIndex, row.meta.innerRowIndexEnd)
}

function addBlankCell(
  nodesWithIndex: RecordNodeWithInnerRowIndex[],
  rowInnerRowIndexEnd: number,
): RecordNodeWithInnerRowIndex[] {
  const result: RecordNodeWithInnerRowIndex[] = []
  for (const nodeWithIndex of nodesWithIndex) {
    const lastIndex = result.at(-1)?.innerRowIndexEnd ?? 0
    if (nodeWithIndex.innerRowIndexStart === lastIndex) {
      result.push(nodeWithIndex)
    } else {
      const emptyCell: RecordNodeWithInnerRowIndex = {
        innerRowIndexStart: lastIndex,
        innerRowIndexEnd: nodeWithIndex.innerRowIndexStart,
        recordNode: undefined,
        parentRecordNode: undefined,
        height: nodeWithIndex.innerRowIndexStart - lastIndex,
      }
      result.push(emptyCell, nodeWithIndex)
    }
  }
  const lastIndex = result.at(-1)?.innerRowIndexEnd
  if (lastIndex !== undefined && lastIndex !== rowInnerRowIndexEnd) {
    result.push({
      innerRowIndexStart: lastIndex,
      innerRowIndexEnd: rowInnerRowIndexEnd,
      recordNode: undefined,
      parentRecordNode: undefined,
      height: rowInnerRowIndexEnd - lastIndex,
    })
  }

  return result
}

function getParentRecordNodes(rootViewNodeRecord: ViewQueryRecordNode, nodePath: string[]) {
  // eslint-disable-next-line sonarjs/deprecation
  const parentRecordNodes = getRecordNodeFromRootNode(rootViewNodeRecord, nodePath.slice(0, -1))
  if (parentRecordNodes === undefined) {
    // ルートのとき
    return
  }
  const xs = Array.isArray(parentRecordNodes) ? parentRecordNodes : [parentRecordNodes]
  return xs.map((x) => ({
    parent: x,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    children: x.children.find((tableNode) => tableNode.nodeName === nodePath.at(-1)!)?.children ?? [],
  }))
}

export function getRecordNodeWithInnerRowIndex(
  row: RSheetRecordNode,
  node: ViewQueryNode,
  innerRowIndex: number,
): RecordNodeWithInnerRowIndex | undefined {
  const xs = getRecordNodesWithInnerRowIndex(row, node)
  return xs.find((x) => x.innerRowIndexStart <= innerRowIndex && innerRowIndex < x.innerRowIndexEnd)
}

export function getRecordNodeAttributeValue(
  data: RSheetRecordNode[],
  column?: RSheetColumn<RSheetRecordNode>,
  cursor?: {
    rowIndex: number
    columnIndex: number
    innerRowIndex: number
  },
): unknown {
  if (cursor === undefined || column === undefined) {
    return undefined
  }
  const row = data[cursor.rowIndex]
  if (row === undefined) {
    return undefined
  }
  const recordNode = getRecordNodeWithInnerRowIndex(row, column.node, cursor.innerRowIndex)?.recordNode
  if (recordNode === undefined) {
    return undefined
  }

  return column.value(recordNode)
}
